// 화면 그리기(§9.8.2~9.8.3) — `draw` 채널 규약. 원본 3곳을 **함께 바꾼다**:
//   이 파일 · 에이전트 `poc/rtc-core-rust/src/draw.rs` · (원격지 그림판도 이 파일을 쓴다 — 같은 웹 코드).
//
// 에이전트는 내용을 해석하지 않는 중계자다(크기·빈도 상한만). 형식 검사는 **받는 쪽 편집기**가 한다(`model.validObj`).
import { validObj } from './model.ts';
import type { Author, DrawObj, Op } from './model.ts';

export const DRAW_CHANNEL_LABEL = 'draw';
export const DRAW_PROTOCOL_VERSION = 1;
/** 메시지 한 건 상한(바이트, UTF-16 길이로 어림). 에이전트도 같은 값으로 자른다. */
export const MAX_MESSAGE_BYTES = 64 * 1024;
/** 획 점·이동을 묶어 보내는 간격(ms) — 초당 30여 건. 마우스 이벤트마다 보내면 초당 수백 건이 된다. */
export const BATCH_MS = 30;
/**
 * 켜기를 보낸 뒤 원격지 그림판의 응답(`caps`)을 기다리는 시한(ms). 무인 서비스 기기는 앱이 꺼져 있으면 트레이가 앱을
 * 새로 띄운다(D3b — WebView2 기동까지 수 초). 끝내 오지 않으면(로그인 화면·트레이 없음 등) "내 화면에만" 으로 내려간다 —
 * "여는 중…" 에 영원히 머무는 것보다 정직하다. 늦게라도 오면 그때 "상대 화면에도 표시" 로 바뀐다.
 */
export const REMOTE_OPEN_TIMEOUT_MS = 30_000;

/** 에이전트 → 뷰어: 채널이 열리면 1회. */
export interface HelloMsg {
  t: 'hello';
  v: number;
  /** `app` = 원격지 그림판을 띄울 통합앱이 있다 · `none` = 없다(뷰어에서만 그린다). */
  ui: 'app' | 'none';
}

/**
 * 원격지 그림판 → 뷰어: 그림판 창을 만든 뒤 1회. **캡처 제외를 실제로 걸어 본 결과**를 알린다
 * (에이전트가 OS 버전으로 짐작하지 않는다 — 걸어 보고 되읽은 값이 진실이다).
 * `excluded=false` 면 원격지 그림이 영상에 찍혀 오므로 뷰어는 원격지 작성분을 자기 캔버스에 **그리지 않는다**(이중 표시 방지).
 */
export interface CapsMsg {
  t: 'caps';
  excluded: boolean;
}

/** 채널 메시지. 문서 연산(`Op`)은 그대로 싣는다. */
export type DrawMsg =
  | HelloMsg
  | CapsMsg
  | Op
  | { t: 'on'; by: Author }
  | { t: 'off'; by: Author }
  | { t: 'laser'; by: Author; p: [number, number] }
  | { t: 'sync'; objs: DrawObj[]; board: boolean }
  /** 전체 상태를 보내 달라(뷰어가 재접속·그리기를 다시 켤 때). */
  | { t: 'syncreq' }
  /** 원격지 사람이 지금 그리는 중인가(마우스 주인 표시). */
  | { t: 'busy'; by: Author; on: boolean };

const isNum01 = (v: unknown) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
const isAuthor = (v: unknown): v is Author => v === 'v' || v === 'r';
const isId = (v: unknown) => typeof v === 'string' && /^[vr]\d{1,9}$/.test(v);

/**
 * 받은 문자열 → 메시지. 형식이 틀리면 null(버린다 — 채널 너머는 다른 프로그램이다).
 * 객체 내용 검사(`validObj`)는 여기서도 한 번 하고, 문서가 적용할 때 또 한다.
 */
export function parseDrawMsg(text: string): DrawMsg | null {
  if (text.length > MAX_MESSAGE_BYTES) return null;
  let m: Record<string, unknown>;
  try {
    m = JSON.parse(text);
  } catch {
    return null;
  }
  if (!m || typeof m !== 'object') return null;
  switch (m.t) {
    case 'hello':
      return typeof m.v === 'number' && (m.ui === 'app' || m.ui === 'none') ? (m as unknown as HelloMsg) : null;
    case 'caps':
      return typeof m.excluded === 'boolean' ? (m as unknown as CapsMsg) : null;
    case 'on':
    case 'off':
      return isAuthor(m.by) ? (m as unknown as DrawMsg) : null;
    case 'laser':
      return isAuthor(m.by) && Array.isArray(m.p) && m.p.length === 2 && isNum01(m.p[0]) && isNum01(m.p[1])
        ? (m as unknown as DrawMsg)
        : null;
    case 'sync':
      return Array.isArray(m.objs) && typeof m.board === 'boolean'
        ? { t: 'sync', objs: (m.objs as unknown[]).filter(validObj), board: m.board }
        : null;
    case 'syncreq':
      return { t: 'syncreq' };
    case 'busy':
      return isAuthor(m.by) && typeof m.on === 'boolean' ? (m as unknown as DrawMsg) : null;
    case 'add':
      return Array.isArray(m.objs) && m.objs.every(validObj) ? (m as unknown as DrawMsg) : null;
    case 'del':
      return Array.isArray(m.ids) && m.ids.every(isId) ? (m as unknown as DrawMsg) : null;
    case 'upd':
      return isId(m.id) && !!m.patch && typeof m.patch === 'object' ? (m as unknown as DrawMsg) : null;
    case 'clear':
      return m.layer === 'screen' || m.layer === 'board' ? (m as unknown as DrawMsg) : null;
    case 'board':
      return typeof m.on === 'boolean' ? (m as unknown as DrawMsg) : null;
    case 'begin':
      return validObj(m.obj) && (m.obj.kind === 'pen' || m.obj.kind === 'marker') ? (m as unknown as DrawMsg) : null;
    case 'pts':
      return isId(m.id) && Array.isArray(m.p) && m.p.length % 2 === 0 && m.p.every(isNum01) ? (m as unknown as DrawMsg) : null;
    case 'end':
      return isId(m.id) ? (m as unknown as DrawMsg) : null;
    default:
      return null;
  }
}

/** 문서 연산인가(편집기에 넘길 것). */
export function isOp(m: DrawMsg): m is Op {
  return ['add', 'del', 'upd', 'clear', 'begin', 'pts', 'end', 'board'].includes(m.t);
}

/**
 * 보내기 묶음 — 같은 획의 `pts` 는 이어 붙이고, 같은 객체의 `upd`(끌어 옮기기)는 마지막 것만 남겨 `BATCH_MS` 마다 보낸다.
 * 🔴 **순서를 지킨다** — 묶을 수 없는 메시지(`end`·`add`…)가 오면 쌓인 것을 먼저 보낸 뒤 보낸다(`end` 가 마지막 점보다 앞서면 획 끝이 잘린다).
 */
export class DrawOutbox {
  private queue: DrawMsg[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly write: (text: string) => void;
  private readonly schedule: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;

  /**
   * @param schedule 타이머(시험에서 바꿔 끼운다).
   * 🔴 기본값은 `setTimeout` 을 **그대로 넘기지 않고 감싼다** — 필드에 담아 `this.schedule(…)` 로 부르면 `this` 가 이 객체가 되어
   *    브라우저에서 "Illegal invocation" 으로 실패한다(Node 는 통과해 단위 시험이 못 잡았다 — 2026-09-26 원격지 그림판 시험에서 발견).
   *    그 결과 획 점·끌어 옮기기·레이저가 실시간으로 가지 않고, 다음 확정 메시지가 올 때에야 한꺼번에 갔다(옮기기만 하면 영영 안 갔다).
   */
  constructor(
    write: (text: string) => void,
    schedule: (fn: () => void, ms: number) => ReturnType<typeof setTimeout> = (fn, ms) => setTimeout(fn, ms),
  ) {
    this.write = write;
    this.schedule = schedule;
  }

  send(m: DrawMsg): void {
    const last = this.queue[this.queue.length - 1];
    if (m.t === 'pts' && last?.t === 'pts' && last.id === m.id) {
      last.p = [...last.p, ...m.p];
    } else if (m.t === 'upd' && last?.t === 'upd' && last.id === m.id) {
      last.patch = { ...last.patch, ...m.patch };
    } else if (m.t === 'laser' && last?.t === 'laser') {
      this.queue[this.queue.length - 1] = m; // 레이저는 최신 점만 의미 있다(꼬리는 받는 쪽이 만든다)
    } else if (m.t === 'pts' || m.t === 'upd' || m.t === 'laser') {
      this.queue.push(m.t === 'pts' ? { ...m, p: [...m.p] } : m);
    } else {
      this.queue.push(m);
      this.flush();
      return;
    }
    if (!this.timer) this.timer = this.schedule(() => this.flush(), BATCH_MS);
  }

  flush(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const q = this.queue;
    this.queue = [];
    for (const m of q) {
      for (const part of splitMsg(m)) this.write(part);
    }
  }
}

/**
 * 메시지를 상한 안으로 나눈다 → JSON 문자열들. 객체 여럿을 싣는 `add`·`sync` 만 나눌 수 있다
 * (`sync` 는 첫 조각만 `sync` — 받는 쪽이 문서를 비우고 싣는다 — 나머지는 `add`).
 * 나눌 수 없는데 넘치는 메시지는 버린다(객체 하나는 `LIMITS.maxPoints` 로 상한 안에 들어오게 돼 있다).
 */
export function splitMsg(m: DrawMsg): string[] {
  const text = JSON.stringify(m);
  if (text.length <= MAX_MESSAGE_BYTES) return [text];
  if (m.t !== 'add' && m.t !== 'sync') return [];
  const out: string[] = [];
  let chunk: DrawObj[] = [];
  let size = 0;
  const emit = () => {
    const first = out.length === 0;
    const msg: DrawMsg = m.t === 'sync' && first ? { t: 'sync', objs: chunk, board: m.board } : { t: 'add', objs: chunk };
    if (m.t === 'sync' || chunk.length) out.push(JSON.stringify(msg));
    chunk = [];
    size = 0;
  };
  const budget = MAX_MESSAGE_BYTES - 128; // 봉투(`{"t":"add","objs":[…]}`) 몫
  for (const o of m.objs) {
    const len = JSON.stringify(o).length + 1;
    if (len > budget) continue; // 객체 하나가 넘친다 — 형식상 불가능(상한), 방어로 건너뛴다
    if (size + len > budget && chunk.length) emit();
    chunk.push(o);
    size += len;
  }
  if (chunk.length || out.length === 0) emit();
  return out;
}
